/** @jsx jsx */
import { Flex, jsx } from 'theme-ui'
import './dropzone.css'
import Dropzone from 'react-dropzone-uploader'
import { UploadToAzure, DeleteFile } from './UploadToAzure'
import { useState, useEffect, useContext } from 'react'
import { useQuery } from '@apollo/client'
import { CfnButton } from '../../../Buttons/index'
import { Close } from '../../../../../icons/index'
import LoadingSpinner from '../../../LoadingSpinner'
import { useAuth } from '../../../../../utils/useAuth'
import { getTimestampInUTC } from '../../../../../utils/globalHelper'
import FILE_UPLOAD_SAS, {
  SAS_PERMISSION,
} from '../../../../../apollo/queries/FILE_UPLOAD_SAS'
import { MaintenancePackageContext } from '../../../../../context/MaintenancePackageContext'
import { DROPZONE_MESSAGE } from './DropZoneMessage'

const DropZone = ({ ...props }) => {
  const [droppedFileCount, setDroppedFileCount] = useState(0)
  const [totalFileCount, setTotalFileCount] = useState(0)
  const [disableDropzone, setDisableDropzone] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [fileInProcess, setFileInProcess] = useState(null)
  const { uploadFileList, dispatch } = useContext(MaintenancePackageContext)
  const MAX_ALLOW_NUM_FILES = 25
  const MAX_FILE_SIZE = 268435456 //256MB
  const MAX_IMAGE_SIZE = 8388608 //8MB
  const ACCEPT_FILE_TYPE =
    'image/jpeg, image/png, image/gif, image/jpg, text/plain, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/pdf, application/vnd.openxmlformats-officedocument.presentationml.presentation, application/vnd.ms-powerpoint, audio/mpeg, video/mp4, video/quicktime, video/x-ms-wmv'
  const { user } = useAuth()
  const { refetch } = useQuery(FILE_UPLOAD_SAS, {
    variables: { permission: SAS_PERMISSION.CREATE, filePath: '/' },
  })

  useEffect(() => {
    setDisableDropzone(droppedFileCount + 1 >= MAX_ALLOW_NUM_FILES)
    if (fileInProcess) {
      if (droppedFileCount + 1 <= MAX_ALLOW_NUM_FILES) {
        uploadFile(fileInProcess)
        if (droppedFileCount < MAX_ALLOW_NUM_FILES) {
          setDroppedFileCount(droppedFileCount + 1)
        }
      }
      setTotalFileCount(totalFileCount + 1)
    }
  }, [fileInProcess])

  const handleChangeStatus = async ({ file }, status) => {
    //image max size is 8MB, other file max size is 256MB
    if (
      (file.type?.includes('image') && file.size > MAX_IMAGE_SIZE) ||
      file.size > MAX_FILE_SIZE
    ) {
      setErrorMessage(DROPZONE_MESSAGE.INVALID_FILE_SIZE)
      return
    }
    if (status === 'done') {
      if (uploadFileList?.find(a => a.name === file.name)) {
        setErrorMessage(DROPZONE_MESSAGE.DUPLICATE_FILE)
        return
      }
      setErrorMessage(null)
      setFileInProcess(file)
    }
  }

  const handleErrorMessage = (extra, isInitial) => {
    if (totalFileCount > MAX_ALLOW_NUM_FILES) {
      return DROPZONE_MESSAGE.EXCEED_MAX_NUM_FILES
    }
    if (totalFileCount === MAX_ALLOW_NUM_FILES) {
      return DROPZONE_MESSAGE.MAX_NUM_FILES
    }
    if (extra.reject) {
      return DROPZONE_MESSAGE.INVALID_FILE_TYPE
    }
    if (errorMessage) {
      return errorMessage
    }
    if (isInitial) {
      return DROPZONE_MESSAGE.DEFAULT
    }
    return DROPZONE_MESSAGE.DEFAULT_ADD_FILES
  }

  const uploadFile = async file => {
    const timestamp = getTimestampInUTC()
    const filePath = user.ssoUserId + '_' + timestamp + '_' + file.name
    dispatch({ type: 'add', value: { name: file.name, isLoading: true } })
    const result = await refetch({
      permission: SAS_PERMISSION.CREATE,
      filePath: '',
    })
    UploadToAzure(result?.data?.azureFileUploadSas, file, filePath).then(() => {
      dispatch({ type: 'removeByName', value: file.name })
      dispatch({
        type: 'add',
        value: { name: file.name, path: filePath, isLoading: false },
      })
    })
  }
  const removeFile = async name => {
    const filePath = uploadFileList?.find(a => a.name === name)?.path
    if (filePath) {
      const result = await refetch({
        permission: SAS_PERMISSION.DELETE,
        filePath: filePath,
      })
      DeleteFile(result?.data?.azureFileUploadSas, filePath).then(() => {
        if (droppedFileCount - 1 < MAX_ALLOW_NUM_FILES) {
          setDisableDropzone(false)
        }
        setTotalFileCount(totalFileCount - 1)
        setDroppedFileCount(droppedFileCount - 1)
        dispatch({ type: 'removeByName', value: name })
      })
    }
  }

  return (
    <Flex sx={{ flexDirection: 'column' }} {...props}>
      <Flex sx={{ flexWrap: 'wrap' }}>
        {uploadFileList?.map((file, index) => {
          return (
            <CfnButton
              key={index}
              type="button"
              buttonStyle="secondary"
              color="skyLight"
              size="small"
              sx={{
                height: 60,
                mb: 4,
                mr: 2,
              }}
            >
              {file.name}
              {!file.isLoading && (
                <Close
                  sx={{
                    ml: 10,
                  }}
                  height={14}
                  color="#8C8987"
                  onClick={() => removeFile(file.name)}
                />
              )}
              {file.isLoading && <LoadingSpinner />}
            </CfnButton>
          )
        })}
      </Flex>
      <Dropzone
        onChangeStatus={handleChangeStatus}
        timeout={300000} //5mins
        disabled={disableDropzone}
        styles={{
          dropzoneReject: { borderColor: 'red', backgroundColor: '#DAA' },
          inputLabel: extra =>
            extra.reject || errorMessage ? { color: 'red' } : {},
          inputLabelWithFiles: extra =>
            extra.reject || errorMessage ? { color: 'red' } : {},
        }}
        inputWithFilesContent={(files, extra) => {
          return handleErrorMessage(extra)
        }}
        inputContent={(files, extra) => {
          return handleErrorMessage(extra, true)
        }}
        accept={ACCEPT_FILE_TYPE}
      />
    </Flex>
  )
}

export default DropZone
